<template>
  <div class="mdlist-list">
    <!-- Header Row with Headline, Add New List Input, Dropdown, and Button -->
    <div class="header-row">
      <h3>Templates<HelpLink topic="templates" title="How metadata templates work" /></h3>
      <div class="add-new-list">
        <input
          type="text"
          v-model="newMdListName"
          placeholder="Add new list"
          class="add-list-input"
        />
        <select v-model="selectedType" class="type-dropdown">
          <option value="experiment">Experiment</option>
          <option value="instrument">Instrument</option>
          <option value="material">Material</option>
          <option value="record">Record</option>
        </select>
        <button @click="CreateMdList" class="add-list-button">Add</button>
      </div>
    </div>

    <!-- Search Input -->
    <input
      type="text"
      v-model="searchQuery"
      placeholder="Search by Name..."
      class="search-input"
    />

    <table class="mdlist-table">
      <thead>
        <tr>
          <th @click="sortMdLists('name')">
            Name
            <i
              v-if="currentSortColumn === 'name'"
              class="filter-arrow"
              :class="setArrowDirection(currentSortDirection)"
            ></i>
          </th>
          <th @click="sortMdLists('description')">
            Description
            <i
              v-if="currentSortColumn === 'description'"
              class="filter-arrow"
              :class="setArrowDirection(currentSortDirection)"
            ></i>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="mdList in paginatedMdLists"
          :key="mdList.id"
          @click="navigateToMdListDetail(mdList.id)"
          class="mdlist-row"
        >
          <td>{{ mdList.name }}</td>
          <td>{{ mdList.description || '-' }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button
        v-for="page in visiblePageNumbers"
        :key="page"
        @click="setPage(page)"
        :class="{ active: page === currentPage }"
      >
        {{ page }}
      </button>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import apiService from "@/services/apiService";
import { API_ENDPOINTS } from "@/services/config";

export default {
  data() {
  return {
    mdLists: [],
    searchQuery: "",
    typeFilter: "",
    currentPage: 1,
    mdListsPerPage: 15,
    currentSortColumn: "name",
    currentSortDirection: "asc",
    showFilters: false,
    sortedMdLists: [],
    newMdListName: "", 
    selectedType: "experiment",
  };
},
  setup() {
    const toast = useToast();
    return { toast };
  },
  created() {
    this.fetchMdLists();
  },
  watch: {
    selectedType() {
      this.currentPage = 1;
      this.fetchMdLists();
    },
  },
  methods: {
    async fetchMdLists() {
      try {
        const response = await apiService.get(API_ENDPOINTS.metadataLists, {
          entity_type: this.selectedType,
        });
        this.mdLists = response;
        this.sortedMdLists = [...response];
        this.sortMdLists(this.currentSortColumn);
      } catch (error) {
        this.toast.error("Failed to fetch metadata lists.");
        console.error("Error fetching metadata lists:", error);
      }
    },
    sortMdLists(column) {
      if (this.currentSortColumn === column) {
        this.currentSortDirection =
          this.currentSortDirection === "asc" ? "desc" : "asc";
      } else {
        this.currentSortDirection = "asc";
        this.currentSortColumn = column;
      }

      this.sortedMdLists.sort((a, b) => {
        if (a[column] < b[column])
          return this.currentSortDirection === "asc" ? -1 : 1;
        if (a[column] > b[column])
          return this.currentSortDirection === "asc" ? 1 : -1;
        return 0;
      });
    },
    setPage(pageNumber) {
      this.currentPage = pageNumber;
    },
    setArrowDirection(sortDirection) {
      return sortDirection === "desc" ? "bi bi-arrow-down" : "bi bi-arrow-up";
    },
    navigateToMdListDetail(mdListId) {
      this.$router.push({
        name: "MdListDetailPage",
        params: { id: mdListId },
        query: { entity_type: this.selectedType },
      });
    },
    async CreateMdList() {
      if (!this.newMdListName || !this.selectedType) {
        this.toast.error("Please fill in the name and type.");
        return;
      }
      try {
        const data = { name: this.newMdListName, entity_type: this.selectedType };
        const response = await apiService.createResource(API_ENDPOINTS.metadataLists, data);
        const createdMdList = response?.metadata_list || response;
        this.$emit("mdListCreated", createdMdList.id);
        this.$router.push({
          name: "MdListDetailPage",
          params: { id: createdMdList.id },
          query: { entity_type: this.selectedType },
        });
        this.toast.success("Metadata list created successfully!");
      } catch (error) {
        this.toast.error("Failed to create metadata list.");
        console.error(error);
      }
    },
  },
  computed: {
    filteredMdLists() {
      return this.sortedMdLists.filter((mdList) => {
        const name = mdList.name?.toLowerCase() || "";
        const description = mdList.description?.toLowerCase() || "";

        const lowerSearch = this.searchQuery.toLowerCase();
        const matchesSearchQuery = name.includes(lowerSearch) || description.includes(lowerSearch);
        const matchesTypeFilter = true;

        return matchesSearchQuery && matchesTypeFilter;
      });
    },
    paginatedMdLists() {
      const start = (this.currentPage - 1) * this.mdListsPerPage;
      const end = start + this.mdListsPerPage;
      return this.filteredMdLists.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredMdLists.length / this.mdListsPerPage);
    },
    visiblePageNumbers() {
      const range = 15;
      let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
      let endPage = startPage + range - 1;

      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - range + 1);
      }

      return Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      );
    },
  },
};
</script>

  

  
  <style scoped lang="scss">
  @import "@/assets/variables.scss";
  
  .mdlist-list {
    max-width: 800px;
    margin: 20px auto;
    padding: 20px;
    background: $secondary-color;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
    .header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    color: $primary-color;
  }

  .add-new-list {
  display: flex;
  align-items: center;

  .add-list-input,
  .type-dropdown {
    padding: 5px;
    margin-right: 10px;
    border: 1px solid $primary-color;
    border-radius: 4px;
    cursor: pointer;
  }

  .add-list-button {
    padding: 5px 10px;
    background-color: $primary-color;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background-color: darken($primary-color, 10%);
    }
  }
}
}
  
    .mdlist-table{
      margin: 20px auto;
      padding: 20px;
      width: 100%;
    }
  
    .mdlist-table th {
      background-color: $form-color;
      border: 1px solid $primary-color;
      cursor: pointer;
    }
  
    .mdlist-row {
      background-color: $secondary-color;
      cursor: pointer;
    }
  
    .mdlist-row:hover {
      background-color: $form-color;
    }
  
    .pagination {
      display: flex;
      justify-content: center;
      margin-top: 20px;
    }
  
    .pagination button {
      margin: 0 5px;
      padding: 5px 10px;
      background-color: $primary-color;
      border: 1px solid $secondary-color;
      border-radius: 5px;
      cursor: pointer;
    }
  
    .pagination button:hover,
    .pagination button.active {
      background-color: darken($primary-color, 10%);
    }
  }
  </style>
  